import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import StarRating from "@/components/StarRating";
import { Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import confetti from "canvas-confetti";

interface RatingModalProps {
  open: boolean;
  onClose: () => void;
  threadId: string;
  loadId: string;
  partnerName: string;
  partnerCompany: string | null;
  partnerType: string;
  partnerId: string;
  loadIdentifier?: string | null;
  origin?: string;
  destination?: string;
  completedAt?: Date | string | null;
  allowOverride?: boolean;
}

export default function RatingModal({
  open,
  onClose,
  threadId,
  loadId,
  partnerName,
  partnerCompany,
  partnerType,
  partnerId,
  loadIdentifier,
  origin,
  destination,
  completedAt,
  allowOverride = false,
}: RatingModalProps) {
  const { toast } = useToast();
  const [selected, setSelected] = useState<number | null>(null);

  useEffect(() => {
    confetti({
      particleCount: 120,
      spread: 80,
      origin: { y: 0.4 },
      colors: ['#f97316', '#3b82f6', '#10b981', '#f59e0b', '#6366f1'],
    });
  }, []);

  const { data: ratingStatus } = useQuery<{
    myRating: number | null;
    myRatingId: string | null;
    mySkipped: boolean;
    bothResolved: boolean;
  }>({
    queryKey: ["/api/shipment-ratings/thread", threadId],
    enabled: open && !!threadId,
  });

  useEffect(() => {
    if (allowOverride && ratingStatus?.myRating != null && selected === null) {
      setSelected(ratingStatus.myRating);
    }
  }, [allowOverride, ratingStatus?.myRating]);

  const alreadyResolved = ratingStatus?.myRating != null || ratingStatus?.mySkipped;
  const isUpdate = allowOverride && ratingStatus?.myRatingId != null;

  const submitMutation = useMutation({
    mutationFn: async (rating: number) => {
      if (isUpdate && ratingStatus?.myRatingId) {
        return apiRequest("PATCH", `/api/shipment-ratings/${ratingStatus.myRatingId}`, {
          rating,
          threadId,
          ratedId: partnerId,
        });
      }
      return apiRequest("POST", "/api/shipment-ratings", { loadId, threadId, ratedId: partnerId, rating });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/shipment-ratings/thread", threadId] });
      queryClient.invalidateQueries({ queryKey: ["/api/messages/threads"] });
      onClose();
    },
    onError: () => {
      toast({ title: "Failed to submit rating", variant: "destructive" });
    },
  });

  const skipMutation = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/shipment-ratings/skip", { threadId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/shipment-ratings/thread", threadId] });
      queryClient.invalidateQueries({ queryKey: ["/api/messages/threads"] });
      onClose();
    },
    onError: () => {
      toast({ title: "Failed to skip", variant: "destructive" });
    },
  });

  if (!open || (alreadyResolved && !allowOverride)) return null;

  const displayCompany = partnerCompany || partnerName;

  const formattedDate = (() => {
    const d = completedAt ? new Date(completedAt) : new Date();
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  })();

  const hasRoute = origin && destination;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.72)' }}
      data-testid="rating-modal"
    >
      <div className="bg-white dark:bg-gray-950 rounded-2xl max-w-[380px] w-full p-6 shadow-2xl flex flex-col items-center gap-4">

        {/* Decorative dot row */}
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-orange-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-orange-400" />
          <div className="w-2.5 h-2.5 bg-orange-500" style={{ transform: 'rotate(45deg)' }} />
          <div className="w-2.5 h-2.5 rounded-full bg-blue-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
          <div className="w-2.5 h-2.5 rounded-full bg-teal-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-blue-600" />
        </div>

        {/* Green checkmark circle */}
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
          <Check className="w-8 h-8 text-green-700" strokeWidth={2.5} />
        </div>

        {/* Heading */}
        <h2 className="text-2xl font-bold text-center text-gray-900 dark:text-white">
          Shipment complete!
        </h2>

        {/* Load ID + route */}
        {(loadIdentifier || hasRoute) && (
          <p className="text-sm text-gray-500 text-center leading-snug">
            {loadIdentifier && <span>{loadIdentifier}</span>}
            {loadIdentifier && hasRoute && <span> · </span>}
            {hasRoute && <span>{origin} → {destination}</span>}
          </p>
        )}

        {/* Summary card */}
        <div className="w-full bg-gray-50 dark:bg-gray-800 rounded-lg p-3 flex justify-between items-start">
          <div>
            <p className="text-xs text-gray-400 mb-0.5">{partnerType}</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{displayCompany}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400 mb-0.5">Completed</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{formattedDate}</p>
          </div>
        </div>

        {/* How did they do? */}
        <p className="text-sm font-medium text-center text-gray-700 dark:text-gray-300">
          How did {displayCompany} do?
        </p>

        {/* Stars */}
        <StarRating value={selected} onChange={setSelected} size="lg" />

        {/* Buttons */}
        <div className="flex gap-2 w-full">
          <Button
            className="flex-[3]"
            disabled={!selected || submitMutation.isPending}
            onClick={() => selected && submitMutation.mutate(selected)}
            data-testid="button-submit-rating"
          >
            {submitMutation.isPending
              ? (isUpdate ? 'Updating…' : 'Submitting…')
              : (isUpdate ? 'Update Rating' : 'Submit rating')}
          </Button>
          <Button
            variant="outline"
            className="flex-[2]"
            onClick={() => skipMutation.mutate()}
            disabled={skipMutation.isPending}
            data-testid="button-skip-rating"
          >
            {skipMutation.isPending ? 'Skipping…' : 'Skip'}
          </Button>
        </div>

        {/* Bottom caption */}
        <p className="text-xs text-gray-400 text-center">
          This shipment has been added to your completed history.
        </p>
      </div>
    </div>
  );
}
